// @ts-check

/**
 * @file Renders the task daily-work modal form.
 */

import {Form, FormInputs} from "@kaspernj/api-maker/build/form"
import Text from "@kaspernj/api-maker/build/utils/text"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import PropTypes from "prop-types"
import PropTypesExact from "prop-types-exact"
import {memo, useEffect} from "react"
import {View} from "react-native-propforge"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"

import Button from "../../components/button"
import FormDurationInput from "../../components/form/form-duration-input"
import FormTextInput from "../../components/form/form-text-input"
import Modal from "../../components/modal"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} AddDailyWorkModalProps
 * @property {string} [errorText] Validation or save error text.
 * @property {boolean} isVisible Whether the modal is visible.
 * @property {() => unknown} onRequestClose Callback fired for backdrop/cancel close.
 * @property {(values: Record<string, unknown>) => unknown} onSubmit Submit callback.
 * @property {boolean} saving Whether daily work is saving.
 */

/** @typedef {Record<string, never>} AddDailyWorkModalState */

/**
 * Daily-work form shown from the task details screen.
 * @augments {ShapeComponent<AddDailyWorkModalProps, AddDailyWorkModalState>}
 */
class AddDailyWorkModal extends ShapeComponent {
  static propTypes = PropTypesExact({
    errorText: PropTypes.string,
    isVisible: PropTypes.bool.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    saving: PropTypes.bool.isRequired
  })

  static defaultProps = {
    errorText: undefined
  }

  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  form = new FormInputs({})
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** Initializes locale and resets form values when the modal opens. */
  setup() {
    useLocale()
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    const isVisible = this.p.isVisible

    useEffect(() => {
      if (!isVisible) return
      this.tt.form.setValue("hours", "")
      this.tt.form.setValue("comment", "")
    }, [isVisible])
  }

  /**
   * Renders the daily-work modal.
   * @returns {ReactNode} Modal content.
   */
  render() {
    const colors = this.tt.colors
    const themeName = this.tt.themeName

    return (
      <Modal
        backdropTestID="taskShowScreen/addDailyWorkForm/backdrop"
        cardStyle={styles[`card-${themeName}`] ||= {
          gap: 16,
          maxWidth: 420,
          padding: 20
        }}
        isVisible={this.p.isVisible}
        onRequestClose={this.tt.onRequestClose}
        testID="taskShowScreen/addDailyWorkForm"
      >
        <Text
          style={styles[`title-${themeName}`] ||= {
            color: colors.text,
            fontSize: 20,
            fontWeight: 700
          }}
          testID="taskShowScreen/addDailyWorkForm/title"
        >
          {_("Add daily work")}
        </Text>
        <Form
          form={this.tt.form}
          onSubmit={this.tt.onSubmit}
        >
          <View
            style={styles.form ||= {
              gap: 12
            }}
            testID="taskShowScreen/addDailyWorkForm/fields"
          >
            <FormDurationInput
              defaultValue=""
              disabled={this.p.saving}
              label={_("Hours")}
              name="hours"
              testID="taskShowScreen/addDailyWorkForm/hoursInput"
            />
            <FormTextInput
              defaultValue=""
              disabled={this.p.saving}
              label={_("Comment")}
              multiline
              name="comment"
              testID="taskShowScreen/addDailyWorkForm/commentInput"
            />
            {Boolean(this.p.errorText) &&
              <View
                style={styles[`errorCard-${themeName}`] ||= {
                  backgroundColor: colors.dangerBackground,
                  borderColor: colors.danger,
                  borderRadius: 12,
                  borderWidth: 1,
                  padding: 12
                }}
                testID="taskShowScreen/addDailyWorkForm/error"
              >
                <Text
                  style={styles[`errorText-${themeName}`] ||= {
                    color: colors.dangerText,
                    fontWeight: 600
                  }}
                  testID="taskShowScreen/addDailyWorkForm/errorText"
                >
                  {this.p.errorText}
                </Text>
              </View>
            }
            <View
              style={styles.actions ||= {
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 8,
                justifyContent: "flex-end"
              }}
              testID="taskShowScreen/addDailyWorkForm/actions"
            >
              <Button
                disabled={this.p.saving}
                iconName="xmark"
                label={_("Cancel")}
                onPress={this.tt.onRequestClose}
                testID="taskShowScreen/addDailyWorkForm/cancelButton"
                variant="neutral"
              />
              <Button
                disabled={this.p.saving}
                iconName="floppy-disk"
                label={this.p.saving ? _("Saving...") : _("Save")}
                onPress={this.tt.onSubmit}
                testID="taskShowScreen/addDailyWorkForm/saveButton"
                variant="primary"
              />
            </View>
          </View>
        </Form>
      </Modal>
    )
  }

  /** Requests modal close unless a save is in progress. */
  onRequestClose = () => {
    if (this.p.saving) return
    this.p.onRequestClose()
  }

  /**
   * Submits the daily-work form values.
   * @returns {unknown} Submit callback result.
   */
  onSubmit = () => {
    if (this.p.saving) {
      // Save controls are disabled while the submit request is in flight.
      return undefined
    }

    return this.p.onSubmit(
      /** @type {Record<string, unknown>} */ (this.tt.form.asObject())
    )
  }
}

/** @type {NamedExoticComponent<AddDailyWorkModalProps>} */
const AddDailyWorkModalComponent = memo(shapeComponent(AddDailyWorkModal))

export default AddDailyWorkModalComponent
